import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Home from "./pages/Home";
import ProductDetails from "./pages/ProductDetails";
import CategoryProducts from "./pages/CategoryProducts";
import Cart from "./pages/Cart";
import SearchPage from "./component/SearchPage";
import AiChatPage from "./pages/AiChatPage";

// Protected Pages
import CheckoutPage from "./pages/CheckoutPage";
import PaymentPage from "./pages/PaymentPage";
import ProfilePage from "./pages/ProfilePage";
import Address from "./pages/Address";
import UpdateAddress from "./pages/UpdateAddress";
import AddressModel from "./pages/AddressModel";
import WishList from "./pages/WishList";
import OrderSuccess from "./pages/OrderSuccess";
import OrderHistory from "./pages/OrderHistory";
import OrderDetails from "./pages/OrderDetails";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AddProduct from "./pages/admin/AddProduct";
import ManageProducts from "./pages/admin/ManageProducts";
import EditProduct from "./pages/admin/EditProduct";
import AdminRegister from "./pages/admin/AdminRegister";
import AdminOrders from "./pages/admin/AdminOrder";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminChangePassword from "./pages/admin/AdminChangePassword";
import FeatureManagement from "./pages/admin/FeatureManagement";

// Components
import Header from "./component/Header/Header";
import AiFloatingAssistant from "./component/AIAssistant/AiFloatingAssistant";

// Services
import API from "./services/api";
import ProtectedRoute from "./services/ProtectedRoute";
import { isTokenValid } from "./services/isTokenValid";

import "./App.css";

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

function AppContent() {
  const location = useLocation();

  // Hide customer header on admin pages
  const hideNavbar = location.pathname.startsWith("/admin");

  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    isTokenValid(localStorage.getItem("userToken")),
  );
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    if (isLoggedIn && !hideNavbar) {
      fetchCartCount();
    } else {
      setCartCount(0);
    }
  }, [isLoggedIn, hideNavbar]);

  const fetchCartCount = async () => {
    try {
      const response = await API.get("/api/cart");

      setCartCount(response.data.length);
    } catch (error) {
      console.log("Failed to fetch cart count:", error);
    }
  };

  return (
    <>
      {/* =========================
          HEADER
      ========================= */}

      {!hideNavbar && (
        <Header
          cartCount={cartCount}
          isLoggedIn={isLoggedIn}
          setIsLoggedIn={setIsLoggedIn}
        />
      )}

      <div className="page-container">

        {!location.pathname.startsWith("/ai") && <AiFloatingAssistant />}

        <Routes>
          {/*
              PUBLIC ROUTES
              Guests can access these pages
          */}

          {/* Home */}

          <Route
            path="/"
            element={
              <Home setCartCount={setCartCount} isLoggedIn={isLoggedIn} />
            }
          />

          {/* Product Details */}

          <Route
            path="/product/:id"
            element={<ProductDetails setCartCount={setCartCount} />}
          />

          {/* Login */}

          <Route
            path="/login"
            element={<Login setIsLoggedIn={setIsLoggedIn} />}
          />

          {/* Register */}

          <Route path="/register" element={<Register />} />

          {/* Category Products */}

          <Route
            path="/category/:id"
            element={
              <CategoryProducts
                setCartCount={setCartCount}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          {/* Search */}

          <Route path="/search" element={<SearchPage />} />

          {/* AI */}

          <Route
            path="/ai"
            element={<AiChatPage setCartCount={setCartCount} />}
          />

          {/* ==================================================
              PROTECTED ROUTES
              Login required
          ================================================== */}

          <Route element={<ProtectedRoute />}>
            {/* Cart */}

            <Route
              path="/cart"
              element={<Cart setCartCount={setCartCount} />}
            />

            {/* Checkout */}

            <Route path="/checkout" element={<CheckoutPage />} />

            {/* Profile */}

            <Route path="/profile" element={<ProfilePage />} />

            {/* Address */}

            <Route path="/address" element={<Address />} />

            {/* Update Address */}

            <Route path="/update-address" element={<UpdateAddress />} />

            {/* Choose Location */}

            <Route path="/choose-location" element={<AddressModel />} />

            {/* Wishlist */}

            <Route path="/wishlist" element={<WishList />} />

            {/* Payment */}

            <Route path="/payment" element={<PaymentPage />} />

            {/* Order Success */}

            <Route path="/order-success" element={<OrderSuccess />} />

            {/* Order History */}

            <Route path="/orders" element={<OrderHistory />} />

            {/* Order Details */}

            <Route path="/order/:id" element={<OrderDetails />} />
          </Route>

          {/* ==================================================
              ADMIN ROUTES
          ================================================== */}

          {/* Admin Login */}

          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Admin Dashboard */}

          <Route path="/admin/dashboard" element={<AdminDashboard />} />

          {/* Add Product */}

          <Route path="/admin/add-product" element={<AddProduct />} />

          {/* Manage Products */}

          <Route path="/admin/home" element={<ManageProducts />} />

          {/* Edit Product */}

          <Route path="/admin/edit-product/:id" element={<EditProduct />} />

          {/* Admin Register */}

          <Route path="/admin/register" element={<AdminRegister />} />

          {/* Admin Orders */}

          <Route path="/admin/orders" element={<AdminOrders />} />

          {/* Admin Users */}

          <Route path="/admin/users" element={<AdminUsers />} />

          {/* Feature Management */}

          <Route
            path="/admin/feature-management"
            element={<FeatureManagement />}
          />

          {/* Admin Change Password */}

          <Route
            path="/admin/change-password/:id"
            element={<AdminChangePassword />}
          />
        </Routes>
      </div>
    </>
  );
}

export default App;
